import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Skeleton,
} from "@mui/material";
import {
  FaCoins,
  FaBrain,
  FaCommentDollar,
  FaExternalLinkAlt,
  FaPhoneVolume,
} from "react-icons/fa";
import useApiEp from "../hooks/useApiEp";
import { roundToMaxDecimals } from "../utils/util";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../context/AccountContext";

// Subtle pill badge for plan name
const PlanBadge = ({ label, color }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1.2,
      py: 0.25,
      borderRadius: "20px",
      background: `${color}18`,
      border: `1px solid ${color}40`,
      color: color,
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      lineHeight: 1.6,
    }}
  >
    {label}
  </Box>
);

// Mini stat row used inside the merged card
const MiniRow = ({ label, value, color }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{ fontSize: "0.72rem", color: "text.secondary", opacity: 0.75 }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color }}>
      {value}
    </Typography>
  </Box>
);

// Divider line
const Divider = () => <Box sx={{ borderTop: "1px solid #edf0f4", my: 1.5 }} />;

// Shared card shell
const GlassCard = ({ color, children, sx = {} }) => (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      background: "#ffffff",
      border: "1px solid #e5eaf1",
      borderRadius: "16px",
      boxShadow: `0 8px 24px ${color}0d, 0 1px 3px rgba(15,23,42,0.04)`,
      position: "relative",
      overflow: "hidden",
      height: "100%",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: `0 12px 30px ${color}18, 0 2px 6px rgba(15,23,42,0.06)`,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: `linear-gradient(90deg, ${color}, ${color}65)`,
        borderRadius: "16px 16px 0 0",
      },
      ...sx,
    }}
  >
    {children}
  </Card>
);

// Icon bubble
const IconBubble = ({ icon, color }) => (
  <Box
    sx={{
      width: 42,
      height: 42,
      borderRadius: "12px",
      background: `linear-gradient(135deg, ${color}30, ${color}12)`,
      border: `1px solid ${color}30`,
      color: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.15rem",
      flexShrink: 0,
    }}
  >
    {icon}
  </Box>
);

// Action button
const ActionButton = ({ label, color, link, navigate }) => (
  <Button
    variant="outlined"
    size="small"
    onClick={() => navigate(link)}
    endIcon={<FaExternalLinkAlt size={10} />}
    sx={{
      mt: 2,
      border: `1px solid ${color}35`,
      background: `${color}0a`,
      color: color,
      borderRadius: "10px",
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.72rem",
      px: 2,
      py: 0.6,
      width: "100%",
      "&:hover": {
        background: `${color}18`,
        border: `1px solid ${color}55`,
        transform: "translateY(-1px)",
      },
      transition: "all 0.2s ease",
    }}
  >
    {label}
  </Button>
);

const BalanceStatus = ({ color, children }) => (
  <Box
    sx={{
      mt: 2,
      minHeight: 31,
      px: 1.25,
      py: 0.75,
      borderRadius: 2,
      bgcolor: `${color}0b`,
      border: `1px solid ${color}22`,
      color,
      fontSize: "0.7rem",
      lineHeight: 1.4,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
    }}
  >
    {children}
  </Box>
);

// Expiry helper
const formatExpiry = (dateStr) => {
  if (!dateStr) return "NA";
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatsCards = () => {
  const { selectedAccount } = useAccount();
  const { data: BalanceData, isLoading } = useApiEp(
    `/user/get-account-balance?cid=${selectedAccount}`
  );
  const rootDomain = window.location.hostname;
  const navigate = useNavigate();

  const wabaBalance = BalanceData?.wallet ?? 0;
  const aiBalance = BalanceData?.aiWallet ?? 0;
  const voiceBalance = BalanceData?.voiceCallWallet ?? 0;
  const rcsBalance = BalanceData?.rcsWalletRichMedia ?? 0;
  const plan = BalanceData?.plan ?? "NA";

  const marketingMessages = Math.floor(wabaBalance / 0.89);
  const utilityMessages = Math.floor(wabaBalance / 0.115);

  // --- Dynamic Recharge Link Logic ---
  let wabaRechargeLink = selectedAccount
    ? `/reseller-recharge/${selectedAccount}`
    : `/home/wallet-recharge`;

  // Check if WA plan is expired
  const isWaExpired = BalanceData?.waPlanExpiry
    ? new Date(BalanceData.waPlanExpiry) < new Date()
    : false;

  // Route to pricing plans if expired
  if (isWaExpired) {
    wabaRechargeLink = "/pricing-plans";
  }
  // ------------------------------------

  // UI enhancement: Show Skeletons while API is fetching data
  if (isLoading) {
    return (
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={6} lg={3} key={i}>
            <Skeleton
              variant="rounded"
              height={190}
              sx={{
                borderRadius: "16px",
                background: "rgba(255,255,255,0.8)",
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Helper for CardContent to push the button to the bottom
  const contentSx = {
    p: 2.5,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  };

  return (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      {/* ── 1. WABA + PLAN ─────────────────────────────── */}
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <GlassCard color="#7c3aed">
          <CardContent sx={contentSx}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <IconBubble icon={<FaCoins />} color="#7c3aed" />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  WABA Credits
                </Typography>
                <PlanBadge label={plan} color="#7c3aed" />
              </Box>
            </Box>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.45rem",
                background: "linear-gradient(135deg,#a78bfa,#7c3aed)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              ₹{roundToMaxDecimals(wabaBalance, 2).toLocaleString()}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.7rem",
                color: isWaExpired ? "error.main" : "text.secondary", // Turns red if expired
                opacity: isWaExpired ? 1 : 0.7,
                mt: 0.5,
                fontWeight: isWaExpired ? 600 : 400,
              }}
            >
              Expires {formatExpiry(BalanceData?.waPlanExpiry)}
            </Typography>

            <Divider />

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 0.6, mb: 1 }}
            >
              <MiniRow
                label="~Marketing msgs"
                value={`${marketingMessages.toLocaleString()} (@₹0.89)`}
                color="#a78bfa"
              />
              <MiniRow
                label="~Utility msgs"
                value={`${utilityMessages.toLocaleString()} (@₹0.115)`}
                color="#818cf8"
              />
            </Box>

            {/* flexGrow pushes this to the bottom */}
            <Box sx={{ flexGrow: 1 }} />

            <>
              <ActionButton
                label={isWaExpired ? "Renew Plan" : "Recharge"} // Optional: update button label if expired
                color="#7c3aed"
                link={wabaRechargeLink}
                navigate={navigate}
              />
              <ActionButton
                label="Upgrade Plan"
                color="#7c3aed"
                link="/pricing-plans"
                navigate={navigate}
              />
            </>
          </CardContent>
        </GlassCard>
      </Grid>

      {/* ── 2. AI CREDITS ────────────────────────────────────────────────── */}
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <GlassCard color="#0ea5e9">
          <CardContent sx={contentSx}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <IconBubble icon={<FaBrain />} color="#0ea5e9" />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "text.primary",
                }}
              >
                AI Credits
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "2rem",
                background: "linear-gradient(135deg,#38bdf8,#0ea5e9)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1,
                mb: 1,
              }}
            >
              {roundToMaxDecimals(aiBalance, 0).toLocaleString()}
            </Typography>

            <Typography
              sx={{ fontSize: "0.7rem", color: "text.secondary", opacity: 0.7 }}
            >
              Available AI model credits
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <ActionButton
              label="Request Credits"
              color="#0ea5e9"
              link="/home/ai/request-credits"
              navigate={navigate}
            />
          </CardContent>
        </GlassCard>
      </Grid>

      {/* ── 3. VOICE CALL CREDITS ─────────────────────────────────────────── */}
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <GlassCard color="#10b981">
          <CardContent sx={contentSx}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <IconBubble icon={<FaPhoneVolume />} color="#10b981" />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "text.primary",
                }}
              >
                Voice Credits
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "2rem",
                background: "linear-gradient(135deg,#34d399,#10b981)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1,
                mb: 1,
              }}
            >
              {roundToMaxDecimals(voiceBalance, 0).toLocaleString()}
            </Typography>

            <Typography
              sx={{ fontSize: "0.7rem", color: "text.secondary", opacity: 0.7 }}
            >
              Expires {formatExpiry(BalanceData?.voiceCallWalletExpiry)}
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <BalanceStatus color="#0f8f67">
              Available for your Voice campaigns
            </BalanceStatus>
          </CardContent>
        </GlassCard>
      </Grid>

      {/* ── 4. RCS CREDITS ───────────────────────────────────────────────── */}
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <GlassCard color="#f59e0b">
          <CardContent sx={contentSx}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <IconBubble icon={<FaCommentDollar />} color="#f59e0b" />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "text.primary",
                }}
              >
                RCS Credits
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "2rem",
                background: "linear-gradient(135deg,#fbbf24,#f59e0b)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1,
                mb: 1,
              }}
            >
              {roundToMaxDecimals(rcsBalance, 0).toLocaleString()}
            </Typography>

            <Typography
              sx={{ fontSize: "0.7rem", color: "text.secondary", opacity: 0.7 }}
            >
              Expires {formatExpiry(BalanceData?.rcsWalletRichMediaExpiry)}
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <BalanceStatus color="#b86909">
              Available for your RCS campaigns
            </BalanceStatus>
          </CardContent>
        </GlassCard>
      </Grid>
    </Grid>
  );
};

export default StatsCards;
